import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Card from "@/components/Card";
import Button from "@/components/Button";
import { useLocation } from "@/contexts/LocationContext";
import {
  Search,
  Building2,
  Users,
  MapPin,
  MoreVertical,
  Eye,
  Ban,
  CheckCircle,
  Settings,
  CalendarClock,
  Utensils,
  AlertTriangle,
  Loader2,
  Gift,
  X,
  Package,
  Activity,
} from "lucide-react";
import apiClient from "@/lib/api-client";
import OrganizationHealthPanel from "@/components/OrganizationHealthPanel";

interface AddOn {
  id: string;
  code: string;
  name: string;
  price: number;
  interval: string;
  requiredPlanFeature: string | null;
  /** false = el complemento aún no está en venta (solo lo puede dar el superadmin). */
  isPublished?: boolean;
  status: "active" | "trialing" | "past_due" | "cancelled" | null;
  isComp: boolean;
}

interface OrgDetail {
  id: string;
  name: string;
  slug: string;
  email: string;
  phone?: string;
  subscriptionStatus: string;
  bookingMode?: "tables" | "appointments";
  createdAt: string;
  plan: {
    id: string;
    name: string;
    price: number;
    hasAppointments?: boolean;
  };
  _count: {
    users: number;
    locations: number;
    customers: number;
  };
  addOns: AddOn[];
}

interface Organization {
  id: string;
  name: string;
  slug: string;
  email: string;
  phone?: string;
  subscriptionStatus: string;
  bookingMode?: "tables" | "appointments";
  createdAt: string;
  plan: {
    id: string;
    name: string;
    price: number;
    hasAppointments?: boolean;
  };
  _count: {
    users: number;
    locations: number;
    customers: number;
  };
}

interface PlanOption {
  id: string;
  name: string;
  price: number;
  hasAppointments: boolean;
  isActive: boolean;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export default function Organizations() {
  const { refreshLocations } = useLocation();
  // Deep-link `/admin/organizations/:id` (p. ej. desde "Ver organización" del
  // inbox de soporte de plataforma): abre el modal de detalle directamente
  // por id, sin depender de que la organización esté en la página/búsqueda
  // actual del listado — mismo fetch que ya usa `openDetailModal`.
  const { id: deepLinkOrgId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedOrg, setSelectedOrg] = useState<string | null>(null);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [editForm, setEditForm] = useState<{
    bookingMode: "tables" | "appointments";
    planId: string;
  }>({ bookingMode: "tables", planId: "" });
  const [plans, setPlans] = useState<PlanOption[]>([]);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [editError, setEditError] = useState("");

  // Estado del modal de detalle de organización
  const [detailOrg, setDetailOrg] = useState<OrgDetail | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [detailTab, setDetailTab] = useState<"overview" | "health">(
    "overview",
  );
  const [addonActionId, setAddonActionId] = useState<string | null>(null);
  const [addonMessage, setAddonMessage] = useState<{
    type: "success" | "warning" | "error";
    text: string;
  } | null>(null);

  useEffect(() => {
    fetchOrganizations();
  }, [pagination.page, statusFilter]);

  useEffect(() => {
    if (deepLinkOrgId) openDetailModal(deepLinkOrgId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkOrgId]);

  useEffect(() => {
    // Cargar planes una vez para el modal de edición
    apiClient
      .get("/admin/plans")
      .then((res) => setPlans(res.data.data ?? []))
      .catch((err) => console.error("Error loading plans:", err));
  }, []);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (pagination.page === 1) {
        fetchOrganizations();
      } else {
        setPagination((prev) => ({ ...prev, page: 1 }));
      }
    }, 300);
    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  const fetchOrganizations = async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
      });
      if (searchTerm) params.append("search", searchTerm);
      if (statusFilter) params.append("status", statusFilter);

      const response = await apiClient.get(`/admin/organizations?${params}`);
      setOrganizations(response.data.data);
      setPagination(response.data.pagination);
    } catch (err) {
      setError("Error al cargar organizaciones");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (orgId: string, newStatus: string) => {
    try {
      await apiClient.patch(`/admin/organizations/${orgId}/status`, {
        status: newStatus,
      });
      fetchOrganizations();
      setSelectedOrg(null);
      // Refresh location context so the switcher removes suspended orgs immediately
      refreshLocations();
    } catch (err) {
      console.error(err);
    }
  };

  const openEditModal = (org: Organization) => {
    setEditingOrg(org);
    setEditForm({
      bookingMode: org.bookingMode ?? "tables",
      planId: org.plan.id,
    });
    setEditError("");
    setSelectedOrg(null);
  };

  const closeEditModal = () => {
    setEditingOrg(null);
    setEditError("");
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingOrg) return;
    try {
      setIsSavingEdit(true);
      setEditError("");
      const payload: Record<string, unknown> = {};
      if (editForm.bookingMode !== (editingOrg.bookingMode ?? "tables")) {
        payload.bookingMode = editForm.bookingMode;
      }
      if (editForm.planId !== editingOrg.plan.id) {
        payload.planId = editForm.planId;
      }
      if (Object.keys(payload).length === 0) {
        closeEditModal();
        return;
      }
      await apiClient.put(`/admin/organizations/${editingOrg.id}`, payload);
      await fetchOrganizations();
      closeEditModal();
    } catch (err: unknown) {
      const msg =
        err instanceof Error && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : "Error al guardar los cambios";
      setEditError(msg || "Error al guardar los cambios");
    } finally {
      setIsSavingEdit(false);
    }
  };

  const openDetailModal = async (orgId: string) => {
    setDetailError("");
    setAddonMessage(null);
    setIsLoadingDetail(true);
    setDetailOrg(null);
    setSelectedOrg(null);
    setDetailTab("overview");
    try {
      const res = await apiClient.get(`/admin/organizations/${orgId}`);
      const data: OrgDetail = res.data.data;
      setDetailOrg({ ...data, addOns: data.addOns ?? [] });
    } catch (err) {
      console.error(err);
      setDetailError("Error al cargar el detalle de la organización.");
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const closeDetailModal = () => {
    // Si se llegó por deep-link (`/admin/organizations/:id`), vuelve a la
    // ruta sin id al cerrar — si no, reabriría el mismo modal al re-montar.
    if (deepLinkOrgId) navigate("/admin/organizations", { replace: true });
    setDetailOrg(null);
    setDetailError("");
    setAddonMessage(null);
    setAddonActionId(null);
    setDetailTab("overview");
  };

  const refreshDetail = async (orgId: string) => {
    try {
      const res = await apiClient.get(`/admin/organizations/${orgId}`);
      const data: OrgDetail = res.data.data;
      setDetailOrg({ ...data, addOns: data.addOns ?? [] });
    } catch (err) {
      console.error(err);
    }
  };

  const handleGrantAddon = async (orgId: string, addOnId: string) => {
    setAddonActionId(addOnId);
    setAddonMessage(null);
    try {
      const res = await apiClient.post(
        `/admin/organizations/${orgId}/addons/${addOnId}/grant`,
      );
      const { planCompatible } = res.data.data as {
        planCompatible: boolean;
        organizationId: string;
        addOnId: string;
        status: string;
      };
      if (!planCompatible) {
        setAddonMessage({
          type: "warning",
          text: "Add-on activado, pero el plan actual de la organización no incluye esta función (p.ej. hasVoiceAi). Asígnale un plan compatible para que funcione.",
        });
      } else {
        setAddonMessage({ type: "success", text: "Add-on activado correctamente." });
      }
      await refreshDetail(orgId);
    } catch (err: unknown) {
      const msg =
        err instanceof Error && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : "Error al activar el add-on";
      setAddonMessage({ type: "error", text: msg || "Error al activar el add-on." });
    } finally {
      setAddonActionId(null);
    }
  };

  const handleRevokeAddon = async (orgId: string, addOnId: string) => {
    setAddonActionId(addOnId);
    setAddonMessage(null);
    try {
      await apiClient.post(
        `/admin/organizations/${orgId}/addons/${addOnId}/revoke`,
      );
      setAddonMessage({ type: "success", text: "Add-on retirado correctamente." });
      await refreshDetail(orgId);
    } catch (err: unknown) {
      const msg =
        err instanceof Error && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : "Error al retirar el add-on";
      setAddonMessage({ type: "error", text: msg || "Error al retirar el add-on." });
    } finally {
      setAddonActionId(null);
    }
  };

  const getAddonStatusBadge = (addOn: AddOn) => {
    if (addOn.isComp && addOn.status === "active") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
          <Gift className="h-3 w-3" />
          Activo (regalo)
        </span>
      );
    }
    if (addOn.status === "active") {
      return (
        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">
          Activo
        </span>
      );
    }
    if (addOn.status === "trialing") {
      return (
        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
          Prueba
        </span>
      );
    }
    if (addOn.status === "past_due") {
      return (
        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
          Pago pendiente
        </span>
      );
    }
    if (addOn.status === "cancelled") {
      return (
        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
          Cancelado
        </span>
      );
    }
    return null;
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      active: "bg-green-100 text-green-800",
      trialing: "bg-blue-100 text-blue-800",
      suspended: "bg-red-100 text-red-800",
      cancelled: "bg-gray-100 text-gray-800",
      past_due: "bg-yellow-100 text-yellow-800",
    };
    const labels: Record<string, string> = {
      active: "Activo",
      trialing: "Prueba",
      suspended: "Suspendido",
      cancelled: "Cancelado",
      past_due: "Pago pendiente",
    };
    return (
      <span
        className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status] || styles.active}`}
      >
        {labels[status] || status}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-gray-900">
          Organizaciones
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          Gestiona todas las organizaciones del sistema
        </p>
      </div>

      <Card>
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={20}
            />
            <input
              type="text"
              placeholder="Buscar por nombre, email o slug..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">Todos los estados</option>
            <option value="active">Activo</option>
            <option value="trialing">Prueba</option>
            <option value="suspended">Suspendido</option>
            <option value="cancelled">Cancelado</option>
            <option value="past_due">Pago pendiente</option>
          </select>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="py-12 text-center text-gray-500">Cargando...</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Organización
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Plan
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estadísticas
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Creado
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {organizations.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-6 py-12 text-center text-gray-500"
                      >
                        No se encontraron organizaciones
                      </td>
                    </tr>
                  ) : (
                    organizations.map((org) => (
                      <tr key={org.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="h-10 w-10 flex-shrink-0 bg-primary-100 rounded-lg flex items-center justify-center">
                              <Building2 className="h-5 w-5 text-primary-600" />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                                {org.name}
                                {org.bookingMode === "appointments" ? (
                                  <span
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded bg-purple-100 text-purple-700"
                                    title="Modo citas"
                                  >
                                    <CalendarClock className="h-3 w-3" />
                                    Citas
                                  </span>
                                ) : (
                                  <span
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded bg-amber-100 text-amber-700"
                                    title="Modo restaurante"
                                  >
                                    <Utensils className="h-3 w-3" />
                                    Mesas
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-gray-500">
                                {org.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {org.plan.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {org.plan.price}€/mes
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <span className="flex items-center">
                              <MapPin className="h-4 w-4 mr-1" />
                              {org._count.locations}
                            </span>
                            <span className="flex items-center">
                              <Users className="h-4 w-4 mr-1" />
                              {org._count.users}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {getStatusBadge(org.subscriptionStatus)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {formatDate(org.createdAt)}
                        </td>
                        <td className="px-6 py-4 text-right relative">
                          <button
                            onClick={() =>
                              setSelectedOrg(
                                selectedOrg === org.id ? null : org.id,
                              )
                            }
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <MoreVertical size={20} />
                          </button>
                          {selectedOrg === org.id && (
                            <div className="absolute right-6 top-12 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[180px]">
                              <button
                                onClick={() => openDetailModal(org.id)}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                Ver detalles
                              </button>
                              <button
                                onClick={() => openEditModal(org)}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                              >
                                <Settings className="h-4 w-4 mr-2" />
                                Editar plan / modo
                              </button>
                              {org.subscriptionStatus !== "active" && (
                                <button
                                  onClick={() =>
                                    handleStatusChange(org.id, "active")
                                  }
                                  className="w-full px-4 py-2 text-left text-sm text-green-700 hover:bg-gray-100 flex items-center"
                                >
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                  Activar
                                </button>
                              )}
                              {org.subscriptionStatus === "active" && (
                                <button
                                  onClick={() =>
                                    handleStatusChange(org.id, "suspended")
                                  }
                                  className="w-full px-4 py-2 text-left text-sm text-red-700 hover:bg-gray-100 flex items-center"
                                >
                                  <Ban className="h-4 w-4 mr-2" />
                                  Suspender
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4">
                <div className="text-sm text-gray-500">
                  Mostrando {(pagination.page - 1) * pagination.limit + 1} a{" "}
                  {Math.min(
                    pagination.page * pagination.limit,
                    pagination.total,
                  )}{" "}
                  de {pagination.total}
                </div>
                <div className="flex space-x-2">
                  <Button
                    variant="secondary"
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        page: prev.page - 1,
                      }))
                    }
                    disabled={pagination.page === 1}
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        page: prev.page + 1,
                      }))
                    }
                    disabled={pagination.page === pagination.totalPages}
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Modal: carga de detalle */}
      {isLoadingDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-8 flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-primary-600" />
            <span className="text-gray-700">Cargando detalle…</span>
          </div>
        </div>
      )}

      {/* Modal: detalle de organización con add-ons */}
      {detailOrg && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90dvh] overflow-y-auto">
            {/* Cabecera */}
            <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {detailOrg.name}
                </h2>
                <p className="text-sm text-gray-500 mt-0.5">{detailOrg.email}</p>
              </div>
              <button
                onClick={closeDetailModal}
                className="text-gray-400 hover:text-gray-600 ml-4 flex-shrink-0"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Pestañas */}
            <div className="px-6 border-b border-gray-200">
              <nav className="flex gap-4 -mb-px">
                <button
                  type="button"
                  onClick={() => setDetailTab("overview")}
                  className={`py-3 text-sm font-medium border-b-2 transition ${
                    detailTab === "overview"
                      ? "border-primary-600 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Resumen
                </button>
                <button
                  type="button"
                  onClick={() => setDetailTab("health")}
                  className={`py-3 text-sm font-medium border-b-2 transition flex items-center gap-1.5 ${
                    detailTab === "health"
                      ? "border-primary-600 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <Activity className="h-3.5 w-3.5" />
                  Salud
                </button>
              </nav>
            </div>

            {detailTab === "health" && (
              <div className="p-6">
                <OrganizationHealthPanel organizationId={detailOrg.id} />
              </div>
            )}

            {detailTab === "overview" && (
            <div className="p-6 space-y-6">
              {/* Info básica */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                    Plan
                  </p>
                  <p className="text-sm font-medium text-gray-900">
                    {detailOrg.plan.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {detailOrg.plan.price}€/mes
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                    Estado
                  </p>
                  {getStatusBadge(detailOrg.subscriptionStatus)}
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                    Usuarios
                  </p>
                  <p className="text-sm font-medium text-gray-900 flex items-center gap-1">
                    <Users className="h-4 w-4 text-gray-400" />
                    {detailOrg._count.users}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                    Ubicaciones
                  </p>
                  <p className="text-sm font-medium text-gray-900 flex items-center gap-1">
                    <MapPin className="h-4 w-4 text-gray-400" />
                    {detailOrg._count.locations}
                  </p>
                </div>
              </div>

              {/* Sección add-ons */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Package className="h-4 w-4 text-gray-500" />
                  <h3 className="text-sm font-semibold text-gray-900">
                    Complementos (add-ons)
                  </h3>
                </div>

                {/* Mensaje de feedback */}
                {addonMessage && (
                  <div
                    className={`mb-3 p-3 rounded-lg text-sm flex items-start gap-2 ${
                      addonMessage.type === "success"
                        ? "bg-green-50 border border-green-200 text-green-800"
                        : addonMessage.type === "warning"
                          ? "bg-yellow-50 border border-yellow-200 text-yellow-800"
                          : "bg-red-50 border border-red-200 text-red-700"
                    }`}
                  >
                    <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <span>{addonMessage.text}</span>
                  </div>
                )}

                {detailError && (
                  <div className="mb-3 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">
                    {detailError}
                  </div>
                )}

                {detailOrg.addOns.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">
                    No hay add-ons disponibles en el catálogo.
                  </p>
                ) : (
                  <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
                    {detailOrg.addOns.map((addOn) => {
                      const isActive =
                        addOn.status === "active" || addOn.status === "trialing";
                      const isWorking = addonActionId === addOn.id;
                      const priceLabel = `${addOn.price}€/${addOn.interval === "monthly" ? "mes" : "año"}`;
                      return (
                        <li
                          key={addOn.id}
                          className="flex items-center justify-between gap-3 px-4 py-3 bg-white hover:bg-gray-50"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-medium text-gray-900">
                                {addOn.name}
                              </span>
                              {addOn.isPublished === false && (
                                <span
                                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-gray-100 text-gray-600"
                                  title="Todavía no está en venta: los clientes no lo ven. Se publica en Planes."
                                >
                                  No publicado
                                </span>
                              )}
                              {getAddonStatusBadge(addOn)}
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {priceLabel}
                            </p>
                          </div>
                          <div className="flex-shrink-0">
                            {isActive ? (
                              <Button
                                variant="danger"
                                size="sm"
                                disabled={isWorking}
                                onClick={() =>
                                  handleRevokeAddon(detailOrg.id, addOn.id)
                                }
                              >
                                {isWorking ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  "Quitar"
                                )}
                              </Button>
                            ) : (
                              <Button
                                variant="primary"
                                size="sm"
                                disabled={isWorking}
                                onClick={() =>
                                  handleGrantAddon(detailOrg.id, addOn.id)
                                }
                              >
                                {isWorking ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <>
                                    <Gift className="h-3 w-3 inline mr-1" />
                                    Activar (regalo)
                                  </>
                                )}
                              </Button>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
            )}

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <Button variant="secondary" onClick={closeDetailModal}>
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: editar plan / bookingMode */}
      {editingOrg && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 max-h-[90dvh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-gray-900 mb-1">
              Editar organización
            </h2>
            <p className="text-sm text-gray-500 mb-5">{editingOrg.name}</p>

            <form onSubmit={handleSaveEdit} className="space-y-5">
              {/* Plan */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plan
                </label>
                <select
                  value={editForm.planId}
                  onChange={(e) =>
                    setEditForm((p) => ({ ...p, planId: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {plans
                    .filter(
                      (p) =>
                        p.isActive ||
                        p.id === editingOrg.plan.id /* mantener actual */,
                    )
                    .map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} — {p.price}€/mes
                        {p.hasAppointments ? " · permite citas" : ""}
                      </option>
                    ))}
                </select>
              </div>

              {/* bookingMode */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de negocio
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() =>
                      setEditForm((p) => ({ ...p, bookingMode: "tables" }))
                    }
                    className={`flex flex-col items-start gap-1 p-3 rounded-lg border-2 text-left transition ${
                      editForm.bookingMode === "tables"
                        ? "border-amber-500 bg-amber-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                      <Utensils className="h-4 w-4 text-amber-600" />
                      Restaurante
                    </div>
                    <span className="text-xs text-gray-500">
                      Reservas de mesa
                    </span>
                  </button>
                  <button
                    type="button"
                    disabled={
                      !plans.find((p) => p.id === editForm.planId)
                        ?.hasAppointments
                    }
                    onClick={() =>
                      setEditForm((p) => ({
                        ...p,
                        bookingMode: "appointments",
                      }))
                    }
                    className={`flex flex-col items-start gap-1 p-3 rounded-lg border-2 text-left transition ${
                      editForm.bookingMode === "appointments"
                        ? "border-purple-500 bg-purple-50"
                        : "border-gray-200 hover:border-gray-300"
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    title={
                      plans.find((p) => p.id === editForm.planId)
                        ?.hasAppointments
                        ? ""
                        : "El plan seleccionado no incluye gestión de citas"
                    }
                  >
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                      <CalendarClock className="h-4 w-4 text-purple-600" />
                      Citas
                    </div>
                    <span className="text-xs text-gray-500">
                      Peluquerías, clínicas…
                    </span>
                  </button>
                </div>
                {!plans.find((p) => p.id === editForm.planId)
                  ?.hasAppointments && (
                  <p className="mt-2 text-xs text-gray-500">
                    El plan seleccionado solo permite modo restaurante. Cambia a
                    un plan con gestión de citas para habilitar esa opción.
                  </p>
                )}
              </div>

              {/* Aviso si cambia el modo */}
              {editForm.bookingMode !==
                (editingOrg.bookingMode ?? "tables") && (
                <div className="flex gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-yellow-800">
                    <strong>Cambiar el modo</strong> reconfigura toda la
                    organización: navegación, terminología, emails y widget
                    público se adaptarán al nuevo tipo de negocio. Los datos
                    existentes (reservas, clientes…) no se borran, pero algunos
                    pueden dejar de tener sentido.
                  </div>
                </div>
              )}

              {editError && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">
                  {editError}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="secondary"
                  className="flex-1"
                  onClick={closeEditModal}
                  disabled={isSavingEdit}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={isSavingEdit}
                >
                  {isSavingEdit ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                      Guardando…
                    </>
                  ) : (
                    "Guardar"
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
